import { HttpErrorResponse } from '@angular/common/http';

import { metaAdapter, metaInitState, paginationInitState } from '../meta-adapter';
import { PaginationState } from '../types';
import {
  BaseEntity,
  EntityMetaAdapter,
  EntityState,
  InferScopeMeta,
  MetaActionOptions,
  MetaOptions,
  MetaScopeOptions,
} from './types';
import { resolveScope } from './utils';

const changeMetaBoolean =
  (key: 'markAsPending' | 'markAsLoaded') =>
  <T extends BaseEntity, S extends MetaOptions>(
    state: EntityState<T, S>,
    options?: MetaActionOptions,
  ) => {
    const ids = options?.entities || [];
    const scope = options?.scope || null;

    const entityMetaMap = ids.length
      ? {
          ...state.entityMetaMap,
          ...Object.fromEntries(
            ids.map(id => [id, metaAdapter[key](state.entityMetaMap[id] || metaInitState)]),
          ),
        }
      : state.entityMetaMap;

    const scopeMeta = scope
      ? {
          ...state.scopeMeta,
          [scope]: metaAdapter[key](state.scopeMeta[scope] || metaInitState),
        }
      : state.scopeMeta;

    return <EntityState<T, S>>{
      ...state,
      entityMetaMap,
      scopeMeta,
    };
  };

export const changePaginationMeta = <T extends BaseEntity, S extends MetaOptions>(
  state: EntityState<T>,
  paginationState: PaginationState,
  props: MetaScopeOptions,
) => {
  const scope = resolveScope(props?.scope);
  const scopePagination = {
    ...state.scopePagination,
    [scope]: { ...state.scopePagination[scope], ...paginationState } || paginationInitState,
  };

  return <EntityState<T, S>>{ ...state, scopePagination };
};

// eslint-disable-next-line max-lines-per-function
export const getEntityMetaAdapter = <T extends BaseEntity, S extends MetaOptions>() => {
  const entityMetaAdapter: EntityMetaAdapter<T, S> = {
    markAsPending: changeMetaBoolean('markAsPending'),
    markAsLoaded: changeMetaBoolean('markAsLoaded'),
    setPaginationMeta: changePaginationMeta,
    setError: (state: EntityState<T, S>, error: HttpErrorResponse, options: MetaActionOptions) => {
      const entityIds = options.entities || [];
      const scope = options.scope || null;

      const entityMetaMap = entityIds.length
        ? {
            ...state.entityMetaMap,
            ...Object.fromEntries(
              entityIds.map(entityId => [
                entityId,
                metaAdapter.setError(state.entityMetaMap[entityId] || metaInitState, error),
              ]),
            ),
          }
        : state.entityMetaMap;

      const scopeMeta = scope
        ? {
            ...state.scopeMeta,
            [scope]: metaAdapter.setError(state.scopeMeta[scope] || metaInitState, error),
          }
        : state.scopeMeta;

      return <EntityState<T, S>>{
        ...state,
        entityMetaMap,
        scopeMeta,
      };
    },
    // sets alias for correlationId -> remoteId
    setAlias: (state: EntityState<T, S>, mapping: any) => ({
      ...state,
      aliases: {
        ...mapping,
        ...state.aliases,
      },
    }),
    setScopeMetaCustom: (
      state: EntityState<T, S>,
      data: InferScopeMeta<S>,
      options: MetaScopeOptions,
    ) => {
      const scope = resolveScope(options.scope);
      return <EntityState<T, S>>{
        ...state,
        scopeMeta: {
          ...state.scopeMeta,
          [scope]: {
            ...state.scopeMeta[scope],
            custom: data,
          },
        },
      };
    },
  };
  return entityMetaAdapter;
};
